import axios from 'axios'

// Warns before leaving a form page with edits that haven't been saved.
//
// - Dirty: the user typed or picked something inside the page on one of FORM_ROUTES.
//   Search boxes, table filters and chat inputs don't count.
// - Clean again: after a successful request that looks like a save (see isSaveRequest), and on
//   every completed navigation.
// - Asks only when the user leaves by a link, back/forward, or closing/reloading the tab. Code that
//   redirects after saving (this.$router.push in a .then) goes through without a prompt.
//
// The conversation and journey editors have their own stage-level guard, so they aren't listed.

const FORM_ROUTES = [
  '/createadmin',
  '/new-nudge',
  '/NewKeywordsDetails',
  '/PhrasesDetails',
  '/media-management/upload',
  '/prompt-management/create',
  '/prompt-management/update',
  '/addOrUpdate',
  '/appmessageupdate',
  '/appMessagedetails',
  '/virtual-resources-addOrUpdate',
  '/customer-addOrUpdate',
  '/link-addOrUpdate',
  '/audience-builder/create',
  '/audience-builder/update',
  '/opportunity',
  '/opportunity/:opportunityId?',
  '/opportunity/:opportunityId?/:action?',
  '/reengagement-manager/create',
  '/reengagement-manager/:campaignId?',
  '/offering-manager/create',
  '/offering-manager/update',
  '/user-journey/settings',
  '/scout_jor_detail',
  '/nlp_topic_detail',
  '/nudge-template/create',
  '/nudge-template/update',
  '/nudge-console/create-campaign',
  '/nudge-console/edit-campaign'
]

// Inputs whose changes aren't form edits
const IGNORE_SELECTOR = [
  '.vue-treeselect__input',
  '.vgt-wrap',
  '.nv-search',
  '[data-nv-ignore-dirty]',
  '[class*="chat"]',
  '[class*="search"]'
].join(',')

const SAVE_WORDS = /(add|create|update|save|edit|upsert|delete|remove|activat|deploy|publish|release|approve|submit|upload|resubmit|copy|import|move|link)/i
const READ_WORDS = /(get|fetch|check|list|find|search|count|export|chat|preview|estimate|validate|report|query|filter)/i

function isSaveRequest (config) {
  const method = (config.method || 'get').toUpperCase()
  if (method === 'PUT' || method === 'PATCH' || method === 'DELETE') return true
  if (method !== 'POST') return false
  const path = (config.url || '').split('?')[0]
  const lastSegment = path.split('/').filter(Boolean).pop() || ''
  return SAVE_WORDS.test(lastSegment) && !READ_WORDS.test(lastSegment)
}

function routePattern (route) {
  const matched = route.matched[route.matched.length - 1]
  return matched ? matched.path : route.path
}

export default function installUnsavedChanges (Vue, router) {
  const state = { dirty: false, userNavAt: 0, confirming: false }
  const isFormRoute = route => FORM_ROUTES.indexOf(routePattern(route)) !== -1

  function markDirty (event) {
    if (state.dirty || !isFormRoute(router.currentRoute)) return
    const target = event.target
    if (!target || !target.closest) return
    // Only edits inside the page, not the sidebar or header
    if (!target.closest('.content-wrapper')) return
    if (target.closest(IGNORE_SELECTOR)) return
    if (target.readOnly || target.disabled) return
    state.dirty = true
  }
  document.addEventListener('input', markDirty, true)
  document.addEventListener('change', markDirty, true)
  // Dropdowns don't fire DOM input events; NvSelect and Treeselect report picks through this
  document.addEventListener('nv-field-change', markDirty, true)

  // Remember that the user, not code, started the next navigation
  document.addEventListener('click', event => {
    if (event.target && event.target.closest && event.target.closest('a[href]')) state.userNavAt = Date.now()
  }, true)
  window.addEventListener('popstate', () => { state.userNavAt = Date.now() })

  axios.interceptors.response.use(response => {
    if (state.dirty && response && response.status >= 200 && response.status < 300 && isSaveRequest(response.config || {})) {
      state.dirty = false
    }
    return response
  })

  // Treeselect (direct, and inside NvSelect) emits no DOM events. Its `input` event also fires when
  // the page sets the value from code, so wrap the methods only a user action calls instead.
  Vue.mixin({
    mounted () {
      if (this.$options.name !== 'vue-treeselect') return
      const report = () => {
        if (this.$el) this.$el.dispatchEvent(new CustomEvent('nv-field-change', { bubbles: true }))
      }
      ;['select', 'clear', 'removeLastValue'].forEach(name => {
        const original = this[name]
        if (typeof original !== 'function') return
        this[name] = (...args) => {
          const result = original.apply(this, args)
          report()
          return result
        }
      })
    }
  })

  router.beforeEach((to, from, next) => {
    const userStarted = Date.now() - state.userNavAt < 1500
    // One click starts at most one navigation; a redirect right after "Stay" isn't the user's
    state.userNavAt = 0
    if (!state.dirty || !userStarted || state.confirming || !isFormRoute(from)) return next()
    state.confirming = true
    Vue.prototype.$swal({
      title: 'Leave without saving?',
      text: 'You have changes on this page that haven\'t been saved.',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Leave page',
      cancelButtonText: 'Stay',
      reverseButtons: true
    }).then(result => {
      state.confirming = false
      if (result && result.value) {
        state.dirty = false
        next()
      } else {
        next(false)
      }
    })
  })
  router.afterEach(() => {
    state.dirty = false
    state.userNavAt = 0
  })

  window.addEventListener('beforeunload', event => {
    if (!state.dirty || !isFormRoute(router.currentRoute)) return
    event.preventDefault()
    event.returnValue = ''
  })
}



// WEBPACK FOOTER //
// ./src/plugins/unsavedChanges.js